/* File: EnvironmentBasedRouter.js
 * Path: ananunadm/src/EnvironmentBasedRouter.js
 */
import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import Loading from "./helpers/loading.jsx";
import { useLang } from "./helpers/language.jsx";
import { useUser } from "./helpers/userContext.jsx";
import { userHasModuleMenuAccess, userHasAnyTicketAccess } from "./helpers/permissionResolver.js";
import { AttendanceTopNav } from "./components/Attendance/AttendanceTopNav.jsx";
import { getRuntimeEnabledModules } from "./helpers/tenantConfig";

// Environment configuration
// Modules that should always be routable regardless of the enabled-modules
// whitelist (their sidebar entries are config-driven and shown independently).
const ALWAYS_ON_MODULES = ["forms"];

const getEnabledModules = () => {
	const enabledModules = process.env.REACT_APP_APIT_ENABLED_MODULES;
	if (!enabledModules) {
		return getRuntimeEnabledModules(["all"]);
	}

	const buildModules = enabledModules
		.toLowerCase()
		.split(",")
		.map((module) => module.trim())
		.map((module) => (module === "meeting" ? "meetings" : module));
	return getRuntimeEnabledModules(buildModules);
};

const shouldIncludeModuleRoutes = (moduleName) => {
	const modules = getEnabledModules();
	return modules.includes("all") || modules.includes(moduleName);
};

// Lazy load the AI Layout wrapper
const AiChatLayout = lazy(() => import("./components/ai/AiChatLayout.jsx"));
const FinanceWorkspaceBar = lazy(() => import("./components/finance/workspace/FinanceWorkspaceBar.jsx"));

// Module definitions with their routes and components
const moduleDefinitions = {
	radio: {
		routes: [
			{
				path: "/radio/dashboard",
				component: () => import("./components/radio/radio-dashboard/radioDashboard.jsx"),
				permissionModel: "radioDashboard"
			},
			{
				path: "/radio/track",
				component: () => import("./components/radio/track/list.jsx"),
				permissionModel: "track"
			},
			{
				path: "/radio/track/:id",
				component: () => import("./components/radio/track/page.jsx"),
				permissionModel: "track"
			},
			{
				path: "/radio/stream",
				component: () => import("./components/radio/stream/list.jsx"),
				permissionModel: "stream"
			},
			{
				path: "/radio/stream/:id",
				component: () => import("./components/radio/stream/page.jsx"),
				permissionModel: "stream"
			},
			{
				path: "/radio/playlist",
				component: () => import("./components/radio/playlist/list.jsx"),
				permissionModel: "playlist"
			},
			{
				path: "/radio/playlist/:id",
				component: () => import("./components/radio/playlist/page.jsx"),
				permissionModel: "playlist"
			},
			{
				path: "/radio/advertisement",
				component: () => import("./components/radio/track/adList.jsx"),
				permissionModel: "advertisement"
			},
			{
				path: "/radio/advertisement-playlist",
				component: () => import("./components/radio/playlist/adList.jsx"),
				permissionModel: "advertisementPlaylist"
			},
			{
				path: "/radio/artist",
				component: () => import("./components/radio/artist/list.jsx"),
				permissionModel: "artist"
			},
			{
				path: "/radio/artist/:id",
				component: () => import("./components/radio/artist/page.jsx"),
				permissionModel: "artist"
			}
		]
	},
	flexup: {
		routes: [
			{
				path: "/flexup/dashboard",
				component: () => import("./components/flexup/flexupDashboard.jsx")
			}
		]
	},
	knowledge: {
		routes: [
			{
				path: "/knowledge",
				exact: true,
				component: () => import("./pages/Knowledge/KnowledgeDashboard.jsx"),
				permissionModel: "knowledge"
			},
			{
				path: "/knowledge/articles",
				exact: true,
				component: () => import("./pages/Knowledge/KnowledgeArticlesPage.jsx"),
				permissionModel: "knowledge"
			},
			{
				path: "/knowledge/articles/new",
				component: () => import("./pages/Knowledge/KnowledgeArticleEditor.jsx"),
				permissionModel: "knowledge"
			},
			{
				path: "/knowledge/articles/:id/edit",
				component: () => import("./pages/Knowledge/KnowledgeArticleEditor.jsx"),
				permissionModel: "knowledge"
			},
			{
				path: "/knowledge/articles/:id",
				component: () => import("./pages/Knowledge/KnowledgeArticleView.jsx"),
				permissionModel: "knowledge"
			},
			{
				path: "/knowledge/categories",
				component: () => import("./pages/Knowledge/KnowledgeCategoriesPage.jsx"),
				permissionModel: "knowledge"
			},
			{
				path: "/knowledge/search",
				component: () => import("./pages/Knowledge/KnowledgeSearchPage.jsx"),
				permissionModel: "knowledge"
			},
			{
				path: "/knowledge/auto-docs",
				component: () => import("./pages/Knowledge/KnowledgeAutoDocsPage.jsx"),
				permissionModel: "knowledge"
			},
			{
				path: "/knowledge/settings",
				component: () => import("./pages/Knowledge/KnowledgeSettingsPage.jsx"),
				permissionModel: "knowledge"
			}
		]
	},

	forms: {
		routes: [
			{
				path: "/forms",
				exact: true,
				component: () => import("./pages/Forms/FormsListPage.jsx"),
				permissionModel: "forms"
			},
			{
				path: "/forms/active",
				exact: true,
				component: () => import("./pages/Forms/ActiveSurveysPage.jsx"),
				permissionModel: "forms"
			},
			{
				path: "/forms/create",
				exact: true,
				component: () => import("./pages/Forms/FormBuilderPage.jsx"),
				permissionModel: "forms"
			},
			{
				path: "/forms/:id/edit",
				component: () => import("./pages/Forms/FormBuilderPage.jsx"),
				permissionModel: "forms"
			},
			{
				path: "/forms/:id/preview",
				component: () => import("./pages/Forms/FormPreviewPage.jsx"),
				permissionModel: "forms"
			},
			{
				path: "/forms/:id/responses",
				component: () => import("./pages/Forms/FormResponsesPage.jsx"),
				permissionModel: "forms"
			},
			{
				path: "/forms/:id/analytics",
				component: () => import("./pages/Forms/FormAnalyticsPage.jsx"),
				permissionModel: "forms"
			},
			{
				path: "/forms/answer/:id",
				component: () => import("./pages/Forms/FormAnswerPage.jsx"),
				permissionModel: "forms"
			}
		]
	},

	hr: {
		routes: [
			{
				path: "/hr/users/:userWorkingStatus?",
				component: () => import("./components/hr/user/users.jsx"),
				permissionModel: "user"
			},
			{
				path: "/hr/user/:id",
				component: () => import("./components/hr/user/user.jsx"),
				permissionModel: "user"
			},
			{
				path: "/hr/user-dashboard/:id",
				component: () => import("./components/hr/user-dashboard/userDashboard.jsx"),
				permissionModel: "userDashboard"
			},
			{
				path: "/hr/department",
				component: () => import("./components/hr/department/departments.jsx"),
				permissionModel: "department"
			},
			{
				path: "/hr/department/:id",
				component: () => import("./components/hr/department/department.jsx"),
				permissionModel: "department"
			},
			{
				path: "/hr/position",
				component: () => import("./components/hr/position/positions.jsx"),
				permissionModel: "position"
			},
			{
				path: "/hr/position/:id",
				component: () => import("./components/hr/position/position.jsx"),
				permissionModel: "position"
			},
			{
				path: "/hr/payroll-dashboard",
				exact: true,
				redirect: "/finance/payroll-dashboard"
			},
			{
				path: "/hr/payroll",
				exact: true,
				redirect: "/finance/payroll"
			},
			{
				path: "/hr/payroll/:id",
				component: () => import("./components/hr/payroll/payroll.jsx"),
				permissionModel: "payroll"
			},
			{
				path: "/hr/payroll-analytics/:id",
				component: () => import("./components/hr/payroll/payrollAnalytics.jsx"),
				permissionModel: "payroll"
			},
			{
				path: "/hr/org-tree",
				component: () => import("./components/hr/org-tree/orgTree.jsx"),
				permissionModel: "orgTree"
			},
			{
				path: "/hr/hr-dashboard",
				component: () => import("./components/hr/hr-dashboard/hrDashboard.jsx"),
				permissionModel: "hrDashboard"
			},
			{
				path: "/hr/attendance",
				exact: true,
				component: () => import("./components/hr/attendance/attendanceDashboard.jsx"),
				permissionModel: "attendanceDashboard",
				preserveAttendanceTabs: true
			},
			{
				path: "/hr/attendance/reports",
				exact: true,
				component: () => import("./pages/Attendance/AttendanceReportsPage.jsx"),
				permissionModel: "attendanceDashboard",
				preserveAttendanceTabs: true
			},
			{
				path: "/hr/attendance/calendar",
				exact: true,
				component: () => import("./pages/Attendance/AttendanceCalendarPage.jsx"),
				permissionModel: "attendanceDashboard",
				preserveAttendanceTabs: true
			},
			{
				path: "/hr/attendance-table",
				exact: true,
				component: () => import("./pages/Attendance/LegacyAttendanceReportRedirect.jsx"),
				permissionModel: "attendanceDashboard"
			},
			{
				path: "/hr/attendance-overtime",
				exact: true,
				component: () => import("./pages/Attendance/LegacyAttendanceReportRedirect.jsx"),
				permissionModel: "attendanceDashboard"
			},
			{
				path: "/hr/attendance-time-off",
				exact: true,
				component: () => import("./pages/Attendance/LegacyAttendanceReportRedirect.jsx"),
				permissionModel: "attendanceDashboard"
			},
			{
				path: "/hr/attendance-shift-schedule",
				exact: true,
				component: () => import("./pages/Attendance/LegacyAttendanceReportRedirect.jsx"),
				permissionModel: "attendanceDashboard"
			},
			{
				path: "/hr/attendance-work-time",
				exact: true,
				component: () => import("./pages/Attendance/LegacyAttendanceReportRedirect.jsx"),
				permissionModel: "attendanceDashboard"
			},
			{
				path: "/hr/attendance-manual-status",
				component: () =>
					import("./components/hr/attendanceManualStatus/attendanceManualStatuses.jsx"),
				permissionModel: "attendanceManualStatus"
			},
			{
				path: "/hr/attendance-manual-status/:id",
				component: () =>
					import("./components/hr/attendanceManualStatus/attendanceManualStatus.jsx"),
				permissionModel: "attendanceManualStatus"
			},
			{
				path: "/hr/recruitment/jobs",
				component: () => import("./components/hr/recruitment/jobs.jsx"),
				permissionModel: "recruitment"
			},
			{
				path: "/hr/recruitment/jobs/add",
				component: () => import("./components/hr/recruitment/job.jsx"),
				permissionModel: "recruitment"
			},
			{
				path: "/hr/recruitment/jobs/:id",
				component: () => import("./components/hr/recruitment/job.jsx"),
				permissionModel: "recruitment"
			},
			{
				path: "/hr/recruitment/applications",
				component: () => import("./components/hr/recruitment/applications.jsx"),
				permissionModel: "recruitmentApplication"
			},
			{
				path: "/hr/recruitment/applications/:id",
				component: () => import("./components/hr/recruitment/application.jsx"),
				permissionModel: "recruitmentApplication"
			},
			{
				path: "/hr/recruitment/interviews",
				component: () => import("./components/hr/recruitment/interviews.jsx"),
				permissionModel: "recruitmentInterview"
			},
			{
				path: "/hr/recruitment/interviews/:id",
				component: () => import("./components/hr/recruitment/interview.jsx"),
				permissionModel: "recruitmentInterview"
			},
			{
				path: "/hr/recruitment/assessments",
				component: () => import("./components/hr/recruitment/assessments.jsx"),
				permissionModel: "recruitmentAssessment"
			},
			{
				path: "/hr/recruitment/assessments/:id",
				component: () => import("./components/hr/recruitment/assessment.jsx"),
				permissionModel: "recruitmentAssessment"
			},
			{
				path: "/hr/recruitment/offers",
				component: () => import("./components/hr/recruitment/offers.jsx"),
				permissionModel: "recruitmentOffer"
			},
			{
				path: "/hr/recruitment/offers/:id",
				component: () => import("./components/hr/recruitment/offer.jsx"),
				permissionModel: "recruitmentOffer"
			},
			{
				path: "/hr/recruitment/onboarding",
				component: () => import("./components/hr/recruitment/onboarding.jsx"),
				permissionModel: "recruitmentOnboarding"
			},
			{
				path: "/hr/recruitment/onboarding/:id",
				component: () => import("./components/hr/recruitment/onboardingPlan.jsx"),
				permissionModel: "recruitmentOnboarding"
			},
			{
				path: "/hr/recruitment/dashboard",
				component: () => import("./components/hr/recruitment/dashboard.jsx"),
				permissionModel: "recruitmentDashboard"
			},
			{ path: "/hr/hr-settings", exact: true, redirect: "/system/hr-settings" }
		]
	},

	it: {
		routes: [
			{
				path: "/it/pc",
				component: () => import("./components/it/pc/pcs.jsx"),
				permissionModel: "pc"
			},
			{
				path: "/it/pc/:id",
				component: () => import("./components/it/pc/pc.jsx"),
				permissionModel: "pc"
			},
			{
				path: "/it/laptop",
				component: () => import("./components/it/laptop/laptops.jsx"),
				permissionModel: "laptop"
			},
			{
				path: "/it/laptop/:id",
				component: () => import("./components/it/laptop/laptop.jsx"),
				permissionModel: "laptop"
			},
			{
				path: "/it/cpu",
				component: () => import("./components/it/cpu/cpus.jsx"),
				permissionModel: "cpu"
			},
			{
				path: "/it/cpu/:id",
				component: () => import("./components/it/cpu/cpu.jsx"),
				permissionModel: "cpu"
			},
			{
				path: "/it/gpu",
				component: () => import("./components/it/gpu/gpus.jsx"),
				permissionModel: "gpu"
			},
			{
				path: "/it/gpu/:id",
				component: () => import("./components/it/gpu/gpu.jsx"),
				permissionModel: "gpu"
			},
			{
				path: "/it/ram",
				component: () => import("./components/it/ram/rams.jsx"),
				permissionModel: "ram"
			},
			{
				path: "/it/ram/:id",
				component: () => import("./components/it/ram/ram.jsx"),
				permissionModel: "ram"
			},
			{
				path: "/it/rom",
				component: () => import("./components/it/rom/roms.jsx"),
				permissionModel: "rom"
			},
			{
				path: "/it/rom/:id",
				component: () => import("./components/it/rom/rom.jsx"),
				permissionModel: "rom"
			},
			{
				path: "/it/warranty-provider",
				component: () => import("./components/it/warranty-provider/warrantyProviders.jsx"),
				permissionModel: "warrantyProvider"
			},
			{
				path: "/it/warranty-provider/:id",
				component: () => import("./components/it/warranty-provider/warrantyProvider.jsx"),
				permissionModel: "warrantyProvider"
			},
			{
				path: "/it/router",
				component: () => import("./components/it/network-devices/routers.jsx"),
				permissionModel: "router"
			},
			{
				path: "/it/router/:id",
				component: () => import("./components/it/network-devices/router.jsx"),
				permissionModel: "router"
			},
			{
				path: "/it/telephone",
				component: () => import("./components/it/network-devices/telephones.jsx"),
				permissionModel: "telephone"
			},
			{
				path: "/it/telephone/:id",
				component: () => import("./components/it/network-devices/telephone.jsx"),
				permissionModel: "telephone"
			},
			{
				path: "/it/switch",
				component: () => import("./components/it/network-devices/switchs.jsx"),
				permissionModel: "switch"
			},
			{
				path: "/it/switch/:id",
				component: () => import("./components/it/network-devices/switch.jsx"),
				permissionModel: "switch"
			},
			{
				path: "/it/monitor",
				component: () => import("./components/it/pc/monitor/monitors.jsx"),
				permissionModel: "monitor"
			},
			{
				path: "/it/monitor/:id",
				component: () => import("./components/it/pc/monitor/monitor.jsx"),
				permissionModel: "monitor"
			},
			{
				path: "/it/attendance-device",
				component: () => import("./components/it/network-devices/attendanceDevices.jsx"),
				permissionModel: "pc"
			},
			{
				path: "/it/attendance-device/:id",
				component: () => import("./components/it/network-devices/attendanceDevice.jsx"),
				permissionModel: "pc"
			},
			{
				path: "/it/printer",
				component: () => import("./components/it/printer/printers.jsx"),
				permissionModel: "printer"
			},
			{
				path: "/it/printer/:id",
				component: () => import("./components/it/printer/printer.jsx"),
				permissionModel: "printer"
			},
			{
				path: "/it/smb",
				component: () => import("./components/it/smb/smbList.jsx"),
				permissionModel: "smb"
			},
			{
				path: "/it/smb/:id",
				component: () => import("./components/it/smb/smb.jsx"),
				permissionModel: "smb"
			},
			{
				path: "/it/software",
				component: () => import("./components/it/software/softwares.jsx"),
				permissionModel: "software"
			},
			{
				path: "/it/software/:id",
				component: () => import("./components/it/software/software.jsx"),
				permissionModel: "software"
			},
			{
				path: "/it/it-dashboard",
				component: () => import("./components/it/it-dashboard/itDashboard.jsx"),
				permissionModel: "itDashboard"
			}
		]
	},

	"office-map": {
		routes: [
			{
				path: "/office-map/3d",
				exact: true,
				component: () => import("./components/Office360/OfficeMap3DPlayer.jsx"),
				permissionModel: "office360"
			},
			{
				path: "/office-map/360",
				exact: true,
				component: () => import("./components/Office360/Office360ScenesList.jsx"),
				permissionModel: "office360"
			},
			{
				path: "/office-map/360/add",
				component: () => import("./components/Office360/Office360ScenePage.jsx"),
				permissionModel: "office360"
			},
			{
				path: "/office-map/360/:id/edit",
				component: () =>
					import("./components/Office360/Office360ScenePage.jsx").then((module) => ({
						default: (props) => <module.default {...props} editMode />
					})),
				permissionModel: "office360"
			},
			{
				path: "/office-map/360/:id",
				component: () => import("./components/Office360/Office360ScenePage.jsx"),
				permissionModel: "office360"
			},
			{
				exact: true,
				path: "/office-map",
				component: () => import("./components/office-map/office_map.jsx"),
				permissionModel: "officeMap"
			}
		]
	},

	"world-map": {
		routes: [
			{
				exact: true,
				path: "/world-map",
				component: () => import("./components/world-map/WorldMap.jsx"),
				permissionModel: "worldMap"
			}
		]
	},

	project: {
		routes: [
			{
				path: "/database/projects",
				component: () => import("./components/project/projectList.jsx"),
				permissionModel: "project"
			},
			{
				path: "/database/project/edit/:id",
				component: () => import("./components/project/projectPage.jsx"),
				permissionModel: "project"
			},
			{
				path: "/database/project/steps/:id",
				component: () => import("./components/project/projectSteps.jsx"),
				permissionModel: "project"
			},
			{
				path: "/database/project/add",
				component: () => import("./components/project/projectAdd.jsx"),
				permissionModel: "project"
			},
			{
				path: "/database/project/add/:id",
				component: () => import("./components/project/projectAdd.jsx"),
				permissionModel: "project"
			},
			{
				path: "/database/branch",
				component: () => import("./components/project/branch/branchList.jsx"),
				permissionModel: "branch"
			},
			{
				path: "/database/branch/:id",
				component: () => import("./components/project/branch/branch.jsx"),
				permissionModel: "branch"
			},
			{
				path: "/database/community",
				component: () => import("./components/project/community/communityList.jsx"),
				permissionModel: "community"
			},
			{
				path: "/database/community/:id",
				component: () => import("./components/project/community/community.jsx"),
				permissionModel: "community"
			},
			{
				path: "/database/city",
				component: () => import("./components/project/city/cityList.jsx"),
				permissionModel: "city"
			},
			{
				path: "/database/city/:id",
				component: () => import("./components/project/city/city.jsx"),
				permissionModel: "city"
			},
			{
				path: "/database/village",
				component: () => import("./components/project/village/villageList.jsx"),
				permissionModel: "village"
			},
			{
				path: "/database/village/:id",
				component: () => import("./components/project/village/village.jsx"),
				permissionModel: "village"
			},
			{
				path: "/database/voltage",
				component: () => import("./components/project/voltage/voltageList.jsx"),
				permissionModel: "voltage"
			},
			{
				path: "/database/voltage/:id",
				component: () => import("./components/project/voltage/voltage.jsx"),
				permissionModel: "voltage"
			},
			{
				path: "/database/type",
				component: () => import("./components/project/project-type/projectTypeList.jsx"),
				permissionModel: "projectType"
			},
			{
				path: "/database/search",
				component: () => import("./components/project/search/search.jsx"),
				permissionModel: "project-search"
			},
			{
				path: "/database/type/:id",
				component: () => import("./components/project/project-type/projectType.jsx"),
				permissionModel: "projectType"
			},
			{
				path: "/database/dashboard",
				component: () => import("./components/project/project-dashboard/projectDashboard.jsx"),
				permissionModel: "projectDashboard"
			}
		]
	},

	database: {
		routes: [
			{
				path: "/database/projects",
				component: () => import("./components/project/projectList.jsx"),
				permissionModel: "project"
			},
			{
				path: "/database/project/edit/:id",
				component: () => import("./components/project/projectPage.jsx"),
				permissionModel: "project"
			},
			{
				path: "/database/project/steps/:id",
				component: () => import("./components/project/projectSteps.jsx"),
				permissionModel: "project"
			},
			{
				path: "/database/project/add",
				component: () => import("./components/project/projectAdd.jsx"),
				permissionModel: "project"
			},
			{
				path: "/database/branch",
				component: () => import("./components/project/branch/branchList.jsx"),
				permissionModel: "branch"
			},
			{
				path: "/database/branch/:id",
				component: () => import("./components/project/branch/branch.jsx"),
				permissionModel: "branch"
			},
			{
				path: "/database/community",
				component: () => import("./components/project/community/communityList.jsx"),
				permissionModel: "community"
			},
			{
				path: "/database/community/:id",
				component: () => import("./components/project/community/community.jsx"),
				permissionModel: "community"
			},
			{
				path: "/database/city",
				component: () => import("./components/project/city/cityList.jsx"),
				permissionModel: "city"
			},
			{
				path: "/database/city/:id",
				component: () => import("./components/project/city/city.jsx"),
				permissionModel: "city"
			},
			{
				path: "/database/village",
				component: () => import("./components/project/village/villageList.jsx"),
				permissionModel: "village"
			},
			{
				path: "/database/village/:id",
				component: () => import("./components/project/village/village.jsx"),
				permissionModel: "village"
			},
			{
				path: "/database/voltage",
				component: () => import("./components/project/voltage/voltageList.jsx"),
				permissionModel: "voltage"
			},
			{
				path: "/database/voltage/:id",
				component: () => import("./components/project/voltage/voltage.jsx"),
				permissionModel: "voltage"
			},
			{
				path: "/database/type",
				component: () => import("./components/project/project-type/projectTypeList.jsx"),
				permissionModel: "projectType"
			},
			{
				path: "/database/search",
				component: () => import("./components/project/search/search.jsx"),
				permissionModel: "project-search"
			},
			{
				path: "/database/type/:id",
				component: () => import("./components/project/project-type/projectType.jsx"),
				permissionModel: "projectType"
			},
			{
				path: "/database/dashboard",
				component: () => import("./components/project/project-dashboard/projectDashboard.jsx"),
				permissionModel: "projectDashboard"
			}
		]
	},

	ecommerce: {
		routes: [
			{
				path: "/ecommerce/dashboard",
				component: () => import("./components/e-commerce/dashboard/index"),
				permissionModel: "ecommerceDashboard"
			},
			{
				path: "/ecommerce/brand",
				component: () => import("./components/e-commerce/brand/list.jsx"),
				permissionModel: "brand"
			},
			{
				path: "/ecommerce/brand/:id",
				component: () => import("./components/e-commerce/brand/page.jsx"),
				permissionModel: "brand"
			},
			{
				path: "/ecommerce/category",
				component: () => import("./components/e-commerce/category/list.jsx"),
				permissionModel: "category"
			},
			{
				path: "/ecommerce/category/:id",
				component: () => import("./components/e-commerce/category/page.jsx"),
				permissionModel: "category"
			},
			{
				path: "/ecommerce/field",
				component: () => import("./components/e-commerce/field/list.jsx"),
				permissionModel: "field"
			},
			{
				path: "/ecommerce/field/:id",
				component: () => import("./components/e-commerce/field/page.jsx"),
				permissionModel: "field"
			},
			{
				path: "/ecommerce/product",
				component: () => import("./components/e-commerce/product/list.jsx"),
				permissionModel: "product"
			},
			{
				path: "/ecommerce/product/:id",
				component: () => import("./components/e-commerce/product/page.jsx"),
				permissionModel: "product"
			},
			{
				path: "/ecommerce/sale-product",
				component: () =>
					import("./components/e-commerce/product/list.jsx").then((module) => ({
						default: (props) => <module.default {...props} productType="sale" />
					})),
				permissionModel: "product"
			},
			{
				path: "/ecommerce/shop-product",
				component: () =>
					import("./components/e-commerce/product/list.jsx").then((module) => ({
						default: (props) => <module.default {...props} productType="buying" />
					})),
				permissionModel: "product"
			},
			{
				path: "/ecommerce/store",
				component: () => import("./components/cm/company/list.jsx").then((module) => ({
					default: (props) => <module.default {...props} storeMode />
				})),
				permissionModel: "company"
			},
			{
				path: "/ecommerce/store/:storeId/branches/:id",
				component: () => import("./components/cm/company/storeBranchPage.jsx"),
				permissionModel: "company"
			},
			{
				path: "/ecommerce/store/:id",
				component: () => import("./components/cm/company/page.jsx").then((module) => ({
					default: (props) => <module.default {...props} storeMode />
				})),
				permissionModel: "company"
			},
			{
				path: "/ecommerce/order",
				component: () => import("./components/e-commerce/order/list.jsx"),
				permissionModel: "order"
			},
			{
				path: "/ecommerce/order/:id",
				component: () => import("./components/e-commerce/order/page.jsx"),
				permissionModel: "order"
			},
			{
				path: "/ecommerce/shop-purchase",
				component: () => import("./components/e-commerce/shopping/list.jsx"),
				permissionModel: "shopPurchase"
			},
			{
				path: "/ecommerce/shop-purchase/:id",
				component: () => import("./components/e-commerce/shopping/page.jsx"),
				permissionModel: "shopPurchase"
			},
			{
				path: "/ecommerce/sale",
				component: () => import("./components/e-commerce/sale/list.jsx"),
				permissionModel: "sale"
			},
			{
				path: "/ecommerce/sale/:id",
				component: () => import("./components/e-commerce/sale/page.jsx"),
				permissionModel: "sale"
			},
			{
				path: "/ecommerce/promo-code",
				component: () => import("./components/e-commerce/promo-code/list.jsx"),
				permissionModel: "promoCode"
			},
			{
				path: "/ecommerce/promo-code/:id",
				component: () => import("./components/e-commerce/promo-code/page.jsx"),
				permissionModel: "promoCode"
			},
			{
				path: "/ecommerce/certificate",
				component: () => import("./components/e-commerce/certificate/list.jsx"),
				permissionModel: "certificate"
			},
			{
				path: "/ecommerce/certificate/:id",
				component: () => import("./components/e-commerce/certificate/page.jsx"),
				permissionModel: "certificate"
			},
			{
				path: "/ecommerce/tag",
				component: () => import("./components/e-commerce/tag/list.jsx"),
				permissionModel: "tag"
			},
			{
				path: "/ecommerce/tag/:id",
				component: () => import("./components/e-commerce/tag/page.jsx"),
				permissionModel: "tag"
			}
		]
	},

	crm: {
		routes: [
			{
				path: "/crm/crm-dashboard",
				component: () => import("./components/crm/crm-dashboard/crmDashboard.jsx"),
				permissionModel: "crmDashboard"
			},
			{
				path: "/crm/contact",
				component: () => import("./components/crm/contact/list.jsx"),
				permissionModel: "contact"
			},
			{
				path: "/crm/contact/:id",
				component: () => import("./components/crm/contact/page.jsx"),
				permissionModel: "contact"
			},
			{
				path: "/crm/contact-group",
				component: () => import("./components/crm/contactGroup/list.jsx"),
				permissionModel: "contactGroup"
			},
			{
				path: "/crm/contact-group/:id",
				component: () => import("./components/crm/contactGroup/page.jsx"),
				permissionModel: "contactGroup"
			}
		]
	},

	finance: {
		routes: [
			{
				path: "/finance/dashboard",
				component: () => import("./components/finance/dashboard/FinanceDashboard.jsx"),
				permissionModel: "financeDashboard",
				financeWorkspace: true
			},
			{
				path: "/finance/payroll-dashboard",
				component: () => import("./components/hr/payroll/payrollDashboard.jsx"),
				permissionModel: "payrollDashboard"
			},
			{
				path: "/finance/payroll",
				component: () => import("./components/hr/payroll/payrolls.jsx"),
				permissionModel: "payroll"
			},
			{
				path: "/finance/payroll/:id",
				component: () => import("./components/hr/payroll/payroll.jsx"),
				permissionModel: "payroll"
			},
			{
				path: "/finance/payroll-analytics/:id",
				component: () => import("./components/hr/payroll/payrollAnalytics.jsx"),
				permissionModel: "payroll"
			},
			{
				path: "/finance/budgets",
				component: () => import("./components/finance/budgets/FinanceBudgets.jsx"),
				permissionModel: "financeBudget",
				financeWorkspace: true
			},
			{
				path: "/finance/analytics",
				component: () => import("./components/finance/analytics/FinanceAnalytics.jsx"),
				permissionModel: "financeAnalytics",
				financeWorkspace: true
			},
			{
				path: "/finance/card-issuers",
				component: () => import("./components/finance/cardIssuer/list.jsx"),
				permissionModel: "financeCardIssuer"
			},
			{
				path: "/finance/card-issuer/:id",
				component: () => import("./components/finance/cardIssuer/page.jsx"),
				permissionModel: "financeCardIssuer"
			},
			{
				path: "/finance/cards",
				component: () => import("./components/finance/cards/list.jsx"),
				permissionModel: "financeCard",
				financeWorkspace: true
			},
			{
				path: "/finance/card/:id",
				component: () => import("./components/finance/cards/page.jsx"),
				permissionModel: "financeCard",
				financeWorkspace: true
			},
			{
				path: "/finance/categories",
				component: () => import("./components/finance/categories/list.jsx"),
				permissionModel: "financeCategory",
				financeWorkspace: true
			},
			{
				path: "/finance/category/:id",
				component: () => import("./components/finance/categories/page.jsx"),
				permissionModel: "financeCategory",
				financeWorkspace: true
			},
			{
				path: "/finance/transactions",
				component: () => import("./components/finance/transactions/list.jsx"),
				permissionModel: "financeTransaction",
				financeWorkspace: true
			},
			{
				path: "/finance/transaction/:id",
				component: () => import("./components/finance/transactions/page.jsx"),
				permissionModel: "financeTransaction",
				financeWorkspace: true
			},
			{
				path: "/finance/accounts",
				component: () => import("./components/finance/accounts/list.jsx"),
				permissionModel: "financeAccount",
				financeWorkspace: true
			},
			{
				path: "/finance/account/:id",
				component: () => import("./components/finance/accounts/page.jsx"),
				permissionModel: "financeAccount",
				financeWorkspace: true
			},
			{
				path: "/finance/goals",
				component: () => import("./components/finance/goals/list.jsx"),
				permissionModel: "financeGoal",
				financeWorkspace: true
			},
			{
				path: "/finance/goal/:id",
				component: () => import("./components/finance/goals/page.jsx"),
				permissionModel: "financeGoal",
				financeWorkspace: true
			},
			{
				path: "/finance/loans",
				component: () => import("./components/finance/loans/list.jsx"),
				permissionModel: "financeLoan",
				financeWorkspace: true
			},
			{
				path: "/finance/loan/:id",
				component: () => import("./components/finance/loans/page.jsx"),
				permissionModel: "financeLoan",
				financeWorkspace: true
			},
			{
				path: "/finance/workspaces",
				component: () => import("./components/finance/workspace/FinanceWorkspaces.jsx"),
				permissionModel: "financeWorkspace"
			},
			{
				path: "/finance/settings",
				component: () => import("./components/finance/settings/FinanceSettings.jsx"),
				permissionModel: "financeSettings",
				financeWorkspace: true
			},
			{
				path: "/finance/receipts",
				component: () => import("./components/finance/receipts/list.jsx"),
				permissionModel: "financeReceipt",
				financeWorkspace: true
			},
			{
				path: "/finance/receipt/:id",
				component: () => import("./components/finance/receipts/page.jsx"),
				permissionModel: "financeReceipt",
				financeWorkspace: true
			},
			{
				path: "/finance/recurring",
				component: () => import("./components/finance/recurring/list.jsx"),
				permissionModel: "financeRecurring",
				financeWorkspace: true
			},
			{
				path: "/finance/recurring-item/:id",
				component: () => import("./components/finance/recurring/page.jsx"),
				permissionModel: "financeRecurring",
				financeWorkspace: true
			},
			{
				path: "/finance/rules",
				component: () => import("./components/finance/rules/list.jsx"),
				permissionModel: "financeCategory",
				financeWorkspace: true
			},
			{
				path: "/finance/categorization-rule/:id",
				component: () => import("./components/finance/rules/page.jsx"),
				permissionModel: "financeCategory",
				financeWorkspace: true
			},
			{
				path: "/finance/shopping",
				component: () => import("./components/finance/shopping/FinanceShopping.jsx"),
				permissionModel: "financeShopping",
				financeWorkspace: true
			},
			{
				path: "/finance/platform",
				component: () => import("./components/finance/platform/FinancePlatform.jsx"),
				permissionModel: "financePlatform"
			}
		]
	},

	todo: {
		routes: [
			{
				path: "/todo/boards",
				component: () => import("./components/todo/kanban/kanbans.jsx"),
				permissionModel: "todoTask"
			},
			{
				path: "/todo/board/:boardId",
				exact: true,
				component: () => import("./components/todo/kanban/kanban.jsx"),
				permissionModel: "todoTask"
			},
			{
				path: "/todo/board/:id/edit",
				component: () => import("./components/todo/board/board.jsx"),
				permissionModel: "todoBoard"
			},
			// Backward-compatible aliases for the old Kanban naming/URLs.
			{ path: "/todo/kanban", exact: true, redirect: "/todo/boards" },
			{ path: "/todo/kanban/:boardId", redirect: "/todo/board/:boardId" },
			{ path: "/todo/board", exact: true, redirect: "/todo/boards" },
			{
				path: "/todo/category",
				component: () => import("./components/todo/category/categories.jsx"),
				permissionModel: "todoCategory"
			},
			{
				path: "/todo/category/:id",
				component: () => import("./components/todo/category/category.jsx"),
				permissionModel: "todoCategory"
			},
			{
				path: "/todo/personal",
				component: () => import("./components/todo/kanban/personalBoardRedirect.jsx"),
				permissionModel: "todoTask"
			},
			{ path: "/todo/task", exact: true, redirect: "/todo/personal" },
			{
				path: "/todo/task/:id",
				component: () => import("./components/todo/task/task.jsx"),
				permissionModel: "todoTask"
			},
			{
				path: "/todo/tag",
				component: () => import("./components/todo/tag/tags.jsx"),
				permissionModel: "todoTag"
			},
			{
				path: "/todo/tag/:id",
				component: () => import("./components/todo/tag/tag.jsx"),
				permissionModel: "todoTag"
			},
			{
				path: "/todo/label",
				component: () => import("./components/todo/label/labels.jsx"),
				permissionModel: "todoLabel"
			},
			{
				path: "/todo/label/:id",
				component: () => import("./components/todo/label/label.jsx"),
				permissionModel: "todoLabel"
			},
			{
				path: "/todo/workload-matrix/board-groups",
				component: () => import("./components/todo/workloadMatrix/boardGroups/list.jsx"),
				permissionModel: "todoBoardGroup"
			},
			{
				path: "/todo/workload-matrix/board-groups/:id",
				component: () => import("./components/todo/workloadMatrix/boardGroups/page.jsx"),
				permissionModel: "todoBoardGroup"
			},
			{
				path: "/todo/workload-matrix/category-groups",
				component: () => import("./components/todo/workloadMatrix/categoryGroups/list.jsx"),
				permissionModel: "todoCategoryGroup"
			},
			{
				path: "/todo/workload-matrix/category-groups/:id",
				component: () => import("./components/todo/workloadMatrix/categoryGroups/page.jsx"),
				permissionModel: "todoCategoryGroup"
			},
			{
				path: "/todo/workload-matrix",
				component: () => import("./components/todo/workloadMatrix/WorkloadMatrixPage.jsx"),
				permissionModel: "todoWorkloadMatrix"
			}
		]
	},

	cm: {
		routes: [
			{
				path: "/cm/cm-dashboard",
				component: () => import("./components/cm/cm-dashboard/cmDashboard.jsx"),
				permissionModel: "cmDashboard"
			},
			{
				path: "/cm/address/map",
				component: () => import("./components/cm/address/map/list.jsx"),
				permissionModel: "map"
			},
			{
				path: "/cm/address/map/:id",
				component: () => import("./components/cm/address/map/page.jsx"),
				permissionModel: "map"
			},
			{
				path: "/cm/address/state",
				component: () => import("./components/cm/address/state/list.jsx"),
				permissionModel: "state"
			},
			{
				path: "/cm/address/state/:id",
				component: () => import("./components/cm/address/state/page.jsx"),
				permissionModel: "state"
			},
			{
				path: "/cm/address/city",
				component: () => import("./components/cm/address/city/list.jsx"),
				permissionModel: "city"
			},
			{
				path: "/cm/address/city/:id",
				component: () => import("./components/cm/address/city/page.jsx"),
				permissionModel: "city"
			},
			{
				path: "/cm/address/zip-code",
				component: () => import("./components/cm/address/zip-code/list.jsx"),
				permissionModel: "zipCode"
			},
			{
				path: "/cm/address/zip-code/:id",
				component: () => import("./components/cm/address/zip-code/page.jsx"),
				permissionModel: "zipCode"
			},
			{
				path: "/cm/subscribe/page",
				component: () => import("./components/cm/subscribe/page/index"),
				permissionModel: "subscribePage"
			},
			{
				path: "/cm/subscribe/page/:id",
				component: () => import("./components/cm/subscribe/page/index"),
				permissionModel: "subscribePage"
			},
			{
				path: "/cm/subscribe/step",
				component: () => import("./components/cm/subscribe/step/list.jsx"),
				permissionModel: "subscribeStep"
			},
			{
				path: "/cm/subscribe/step/:id",
				component: () => import("./components/cm/subscribe/step/page.jsx"),
				permissionModel: "subscribeStep"
			},
			{
				path: "/cm/subscribe/faq",
				component: () => import("./components/cm/subscribe/faq/list.jsx"),
				permissionModel: "subscribeFAQ"
			},
			{
				path: "/cm/subscribe/faq/:id",
				component: () => import("./components/cm/subscribe/faq/page.jsx"),
				permissionModel: "subscribeFAQ"
			},
			{
				path: "/cm/contact",
				component: () => import("./components/cm/contact/page/index"),
				permissionModel: "contacts"
			},
			{
				path: "/cm/contact/part",
				component: () => import("./components/cm/contact/part/list.jsx"),
				permissionModel: "contactPart"
			},
			{
				path: "/cm/contact/part/:id",
				component: () => import("./components/cm/contact/part/page.jsx"),
				permissionModel: "contactPart"
			},
			{
				path: "/cm/contact/social",
				component: () => import("./components/cm/contact/social/list.jsx"),
				permissionModel: "contactSocial"
			},
			{
				path: "/cm/contact/social/:id",
				component: () => import("./components/cm/contact/social/page.jsx"),
				permissionModel: "contactSocial"
			},
			{
				path: "/cm/contact/form",
				component: () => import("./components/cm/contact/form/list.jsx"),
				permissionModel: "contacts"
			},
			{
				path: "/cm/contact/form/:id",
				component: () => import("./components/cm/contact/form/page.jsx"),
				permissionModel: "contacts"
			},
			{
				path: "/cm/instagram/page",
				component: () => import("./components/cm/contact/instagram/page/index.jsx"),
				permissionModel: "instagramSection"
			},
			{
				path: "/cm/instagram/page/:id",
				component: () => import("./components/cm/contact/instagram/page/index.jsx"),
				permissionModel: "instagramSection"
			},
			{
				path: "/cm/instagram",
				component: () => import("./components/cm/contact/instagram/list/list.jsx"),
				permissionModel: "instagramSection"
			},
			{
				path: "/cm/instagram/:id",
				component: () => import("./components/cm/contact/instagram/list/page.jsx"),
				permissionModel: "instagramSection"
			},
			{
				path: "/cm/blog/page",
				component: () => import("./components/cm/blog/page/index"),
				permissionModel: "blog"
			},
			{
				path: "/cm/blog/page/:id",
				component: () => import("./components/cm/blog/page/index"),
				permissionModel: "blog"
			},
			{
				path: "/cm/blog/list",
				component: () => import("./components/cm/blog/list/list.jsx"),
				permissionModel: "blog"
			},
			{
				path: "/cm/blog/list/:id",
				component: () => import("./components/cm/blog/list/page.jsx"),
				permissionModel: "blog"
			},
			{
				path: "/cm/events/page",
				component: () => import("./components/cm/events/page/index"),
				permissionModel: "events"
			},
			{
				path: "/cm/events/page/:id",
				component: () => import("./components/cm/events/page/index"),
				permissionModel: "events"
			},
			{
				path: "/cm/events/list",
				component: () => import("./components/cm/events/list/list.jsx"),
				permissionModel: "events"
			},
			{
				path: "/cm/events/list/:id",
				component: () => import("./components/cm/events/list/page.jsx"),
				permissionModel: "events"
			},
			{
				path: "/cm/price-list/page",
				component: () => import("./components/cm/price-list/page/page.jsx"),
				permissionModel: "priceList"
			},
			{
				path: "/cm/price-list/page/:id",
				component: () => import("./components/cm/price-list/page/page.jsx"),
				permissionModel: "priceList"
			},
			{
				path: "/cm/price-list/list",
				component: () => import("./components/cm/price-list/list/list.jsx"),
				permissionModel: "priceList"
			},
			{
				path: "/cm/price-list/list/:id",
				component: () => import("./components/cm/price-list/list/page.jsx"),
				permissionModel: "priceList"
			},
			{
				path: "/cm/for-business/page",
				component: () => import("./components/cm/for-business/page/index"),
				permissionModel: "forBusiness"
			},
			{
				path: "/cm/for-business/page/:id",
				component: () => import("./components/cm/for-business/page/index"),
				permissionModel: "forBusiness"
			},
			{
				path: "/cm/for-business/list",
				component: () => import("./components/cm/for-business/list/list.jsx"),
				permissionModel: "forBusiness"
			},
			{
				path: "/cm/for-business/list/:id",
				component: () => import("./components/cm/for-business/list/page.jsx"),
				permissionModel: "forBusiness"
			},
			{
				path: "/cm/about-us/page",
				component: () => import("./components/cm/about-us/page/index"),
				permissionModel: "about"
			},
			{
				path: "/cm/about-us/page/:id",
				component: () => import("./components/cm/about-us/page/index"),
				permissionModel: "about"
			},
			{
				path: "/cm/about-us/item",
				component: () => import("./components/cm/about-us/items/list.jsx"),
				permissionModel: "aboutUsItem"
			},
			{
				path: "/cm/about-us/item/:id",
				component: () => import("./components/cm/about-us/items/page.jsx"),
				permissionModel: "aboutUsItem"
			},
			{
				path: "/cm/about-us/who-we-are",
				component: () => import("./components/cm/about-us/whoWeAre/index"),
				permissionModel: "who-we-are"
			},
			{
				path: "/cm/about-us/who-we-are/:id",
				component: () => import("./components/cm/about-us/whoWeAre/index"),
				permissionModel: "who-we-are"
			},
			{
				path: "/cm/about-us/our-mission",
				component: () => import("./components/cm/about-us/ourMission/index"),
				permissionModel: "our-mission"
			},
			{
				path: "/cm/about-us/our-mission/:id",
				component: () => import("./components/cm/about-us/ourMission/index"),
				permissionModel: "our-mission"
			},
			{
				path: "/cm/banner/item",
				component: () => import("./components/cm/banner/item/list.jsx"),
				permissionModel: "banner"
			},
			{
				path: "/cm/banner/item/:id",
				component: () => import("./components/cm/banner/item/page.jsx"),
				permissionModel: "banner"
			},
			{
				path: "/cm/banner/slide",
				component: () => import("./components/cm/banner/slide/list.jsx"),
				permissionModel: "banner"
			},
			{
				path: "/cm/banner/slide/:id",
				component: () => import("./components/cm/banner/slide/page.jsx"),
				permissionModel: "banner"
			},
			{
				path: "/cm/faq/page",
				component: () => import("./components/cm/faq/page/index"),
				permissionModel: "faqPage"
			},
			{
				path: "/cm/faq/page/:id",
				component: () => import("./components/cm/faq/page/index"),
				permissionModel: "faqPage"
			},
			{
				path: "/cm/faq/question",
				component: () => import("./components/cm/faq/question/list.jsx"),
				permissionModel: "faqQuestion"
			},
			{
				path: "/cm/faq/question/:id",
				component: () => import("./components/cm/faq/question/page.jsx"),
				permissionModel: "faqQuestion"
			},
			{
				path: "/cm/service/page",
				component: () => import("./components/cm/service/page/index"),
				permissionModel: "servicePage"
			},
			{
				path: "/cm/service/page/:id",
				component: () => import("./components/cm/service/page/index"),
				permissionModel: "servicePage"
			},
			{
				path: "/cm/service/list",
				component: () => import("./components/cm/service/list/list.jsx"),
				permissionModel: "service"
			},
			{
				path: "/cm/service/list/:id",
				component: () => import("./components/cm/service/list/page.jsx"),
				permissionModel: "service"
			},
			{
				path: "/cm/what-you-get",
				component: () => import("./components/cm/what-you-get/list.jsx"),
				permissionModel: "whatYouGet"
			},
			{
				path: "/cm/what-you-get/:id",
				component: () => import("./components/cm/what-you-get/page.jsx"),
				permissionModel: "whatYouGet"
			},
			{
				path: "/cm/review",
				component: () => import("./components/cm/review/list.jsx"),
				permissionModel: "review"
			},
			{
				path: "/cm/review/:id",
				component: () => import("./components/cm/review/page.jsx"),
				permissionModel: "review"
			},
			{
				path: "/cm/company",
				component: () => import("./components/cm/company/list.jsx"),
				permissionModel: "company"
			},
			{
				path: "/cm/company/:id",
				component: () => import("./components/cm/company/page.jsx"),
				permissionModel: "company"
			},
			{
				path: "/cm/services-and-prices/list",
				component: () => import("./components/cm/services-and-prices/list/list.jsx"),
				permissionModel: "servicesAndPrices"
			},
			{
				path: "/cm/services-and-prices/list/:id",
				component: () => import("./components/cm/services-and-prices/list/page.jsx"),
				permissionModel: "servicesAndPrices"
			},
			{
				path: "/cm/certificate-promo-code",
				component: () => import("./components/cm/pages/certificate-promo-code/index"),
				permissionModel: "certificatePromoCode"
			},
			{
				path: "/cm/certificate-promo-code/:id",
				component: () => import("./components/cm/pages/certificate-promo-code/index"),
				permissionModel: "certificatePromoCode"
			},
			{
				path: "/cm/delivery-and-payment",
				component: () => import("./components/cm/pages/delivery/index"),
				permissionModel: "deliveryAndPayment"
			},
			{
				path: "/cm/delivery-and-payment/:id",
				component: () => import("./components/cm/pages/delivery/index"),
				permissionModel: "deliveryAndPayment"
			},
			{
				path: "/cm/privacy-policy",
				component: () => import("./components/cm/pages/privacy-policy/index"),
				permissionModel: "privacyPolicy"
			},
			{
				path: "/cm/privacy-policy/:id",
				component: () => import("./components/cm/pages/privacy-policy/index"),
				permissionModel: "privacyPolicy"
			},
			{
				path: "/cm/terms-and-conditions",
				component: () => import("./components/cm/pages/terms-and-conditions/index"),
				permissionModel: "termsAndConditions"
			},
			{
				path: "/cm/terms-and-conditions/:id",
				component: () => import("./components/cm/pages/terms-and-conditions/index"),
				permissionModel: "termsAndConditions"
			},
			{
				path: "/cm/cookie-policy",
				component: () => import("./components/cm/pages/cookie-policy/index"),
				permissionModel: "cookiePolicy"
			},
			{
				path: "/cm/cookie-policy/:id",
				component: () => import("./components/cm/pages/cookie-policy/index"),
				permissionModel: "cookiePolicy"
			}
		]
	},

	media: {
		routes: [
			{
				path: "/media/photos",
				component: () => import("./components/photos/PhotosPage.jsx"),
				permissionModel: "photos"
			},
			{
				path: "/media/places",
				component: () => import("./components/media/gallery/places.jsx"),
				permissionModel: "galleryMap"
			},
			{
				path: "/media/gallery",
				component: () => import("./components/media/gallery/page.jsx"),
				permissionModel: "gallery"
			},
			{
				path: "/media/map",
				component: () => import("./components/media/gallery/map.jsx"),
				permissionModel: "galleryMap"
			},
			{
				path: "/media/people",
				component: () => import("./components/media/gallery/people.jsx"),
				permissionModel: "galleryPeople"
			}
		]
	},

	files: {
		routes: [
			{
				path: "/files/editor/:fileId",
				component: () => import("./pages/Files/OnlyOfficeEditorPage.jsx"),
				permissionModel: "files"
			},
			{
				path: "/files/browser",
				exact: false,
				component: () => import("./pages/Files/ExplorerFilesBrowser.jsx"),
				permissionModel: "files"
			},
			{
				path: "/files",
				component: () => import("./pages/Files/FilesPage.jsx"),
				permissionModel: "files"
			}
		]
	},

	system: {
		routes: [
			{
				path: "/system/user",
				component: () => import("./components/system/user/list.jsx"),
				permissionModel: "user"
			},
			{
				path: "/system/user/:id",
				component: () => import("./components/hr/user/user.jsx"),
				permissionModel: "user"
			},
			{
				path: "/system/role",
				component: () => import("./components/system/role/list.jsx"),
				permissionModel: "role"
			},
			{
				path: "/system/role/:id",
				component: () => import("./components/system/role/page.jsx"),
				permissionModel: "role"
			},
			{
				path: "/system/platforms",
				component: () => import("./components/system/platform-admin/PlatformAdminPage.jsx"),
				permissionModel: "platformAdmin"
			},
			{
				path: "/system/platforms/:platformKey/users",
				component: () => import("./components/system/platform-admin/users.jsx"),
				permissionModel: "platformAdmin"
			},
			{
				path: "/system/data-retention",
				component: () => import("./components/system/data-retention/DataRetentionPage.jsx"),
				permissionModel: "dataRetention"
			},
			{
				path: "/system/hr-settings",
				component: () => import("./components/hr/settings/hrSettings.jsx"),
				permissionModel: "hrSettings"
			},
			{
				path: "/system/finance-settings",
				component: () => import("./components/finance/settings/FinanceSettings.jsx"),
				permissionModel: "financeSettings"
			},
			{
				path: "/system/chat-settings",
				component: () => import("./components/chat/settings/chatSettings.jsx"),
				permissionModel: "chatSettings"
			},
			{
				path: "/system/files-settings",
				component: () => import("./pages/Files/FileSettings.jsx"),
				permissionModel: "files"
			},
			{
				path: "/system/ticket-settings",
				component: () => import("./components/ticket-system/settings/TicketSettings.jsx"),
				permissionModel: "ticket"
			},
			{
				path: "/system/ai-settings",
				component: () => import("./components/ai/safe-assistant/AiSafetySettings.jsx"),
				permissionModel: "ai"
			}
		]
	},

	"ticket-system": {
		routes: [
			{
				path: "/ticket-system/ticket-dashboard",
				component: () => import("./components/ticket-system/ticket-dashboard/ticketDashboard.jsx"),
				permissionModel: "ticketDashboard"
			},
			{
				path: "/ticket-system/ticket",
				component: () => import("./components/ticket-system/ticket/tickets.jsx"),
				permissionCheck: userHasAnyTicketAccess
			},
			{
				path: "/ticket-system/ticket/:id",
				component: () => import("./components/ticket-system/ticket/ticket.jsx"),
				permissionCheck: userHasAnyTicketAccess
			},
			{
				path: "/ticket-system/problem-group",
				component: () => import("./components/ticket-system/problem-group/problemGroups.jsx"),
				permissionModel: "problemGroup"
			},
			{
				path: "/ticket-system/problem-group/:id",
				component: () => import("./components/ticket-system/problem-group/problemGroup.jsx"),
				permissionModel: "problemGroup"
			},
			{
				path: "/ticket-system/problem",
				component: () => import("./components/ticket-system/problem/problems.jsx"),
				permissionModel: "problem"
			},
			{
				path: "/ticket-system/problem/:id",
				component: () => import("./components/ticket-system/problem/problem.jsx"),
				permissionModel: "problem"
			},
			{
				path: "/ticket-system/ticket-domain",
				component: () => import("./components/ticket-system/ticket-domain/ticketDomains.jsx"),
				permissionModel: "ticketDomain"
			},
			{
				path: "/ticket-system/ticket-domain/:id",
				component: () => import("./components/ticket-system/ticket-domain/ticketDomain.jsx"),
				permissionModel: "ticketDomain"
			},
			{
				path: "/ticket-system/related-list",
				component: () => import("./components/ticket-system/related-list/relatedLists.jsx"),
				permissionModel: "ticketRelatedList"
			},
			{
				path: "/ticket-system/related-list/:id",
				component: () => import("./components/ticket-system/related-list/relatedList.jsx"),
				permissionModel: "ticketRelatedList"
			},
			{
				path: "/ticket-system/priority",
				component: () => import("./components/ticket-system/modules/priority/priorities.jsx"),
				permissionModel: "ticketPriority"
			},
			{
				path: "/ticket-system/priority/:id",
				component: () => import("./components/ticket-system/modules/priority/priority.jsx"),
				permissionModel: "ticketPriority"
			},
			{
				path: "/ticket-system/ticket-type",
				component: () => import("./components/ticket-system/modules/ticket-type/ticketTypes.jsx"),
				permissionModel: "ticketType"
			},
			{
				path: "/ticket-system/ticket-type/:id",
				component: () => import("./components/ticket-system/modules/ticket-type/ticketType.jsx"),
				permissionModel: "ticketType"
			}
		]
	},

	chat: {
		routes: [
			{ path: "/chat", exact: true, redirect: "/chat/chats" },
			{
				path: "/chat/chat-dashboard",
				exact: true,
				redirect: "/chat/chats"
			},
			{
				path: "/chat/chats",
				component: () => import("./components/chat/chat.jsx"),
				permissionModel: "chat",
				// Remount to avoid any stale in-memory state when switching list ↔ conversation (esp. narrow / mobile)
				keyFromPathname: true
			},
			{
				path: "/chat/chat-group/:id",
				component: () => import("./components/chat/chat.jsx"),
				permissionModel: "chat",
				keyFromPathname: true
			}
		]
	},

	"website-chat": {
		routes: [
			{
				path: "/website-chat",
				exact: true,
				component: () => import("./pages/WebsiteChat/WebsiteChatInbox.jsx"),
				permissionModel: "websiteChat"
			},
			{
				path: "/website-chat/sites",
				exact: true,
				component: () => import("./pages/WebsiteChat/WebsiteChatSitesPage.jsx"),
				permissionModel: "websiteChat"
			},
			{
				path: "/website-chat/sites/new",
				component: () => import("./pages/WebsiteChat/WebsiteChatSiteForm.jsx"),
				permissionModel: "websiteChat"
			},
			{
				path: "/website-chat/sites/:id/edit",
				component: () => import("./pages/WebsiteChat/WebsiteChatSiteForm.jsx"),
				permissionModel: "websiteChat"
			},
			{
				path: "/website-chat/settings",
				component: () => import("./pages/WebsiteChat/WebsiteChatSettingsPage.jsx"),
				permissionModel: "websiteChat"
			},
			{
				path: "/website-chat/ai-settings",
				component: () => import("./pages/WebsiteChat/WebsiteChatAISettingsPage.jsx"),
				permissionModel: "websiteChat"
			},
			{
				path: "/website-chat/telegram-settings",
				component: () => import("./pages/WebsiteChat/WebsiteChatTelegramSettingsPage.jsx"),
				permissionModel: "websiteChat"
			},
			{
				path: "/website-chat/canned-replies",
				component: () => import("./pages/WebsiteChat/WebsiteChatCannedRepliesPage.jsx"),
				permissionModel: "websiteChat"
			},
			{
				path: "/website-chat/statistics",
				component: () => import("./pages/WebsiteChat/WebsiteChatStatisticsPage.jsx"),
				permissionModel: "websiteChat"
			}
		]
	},

	// AI module with special layout wrapper flag
	ai: {
		routes: [
			{
				path: "/ai/settings",
				exact: true,
				redirect: "/system/ai-settings"
			},
			{
				path: "/ai",
				exact: false,
				redirect: "/home"
			}
		]
	},

	// Calendar (universal module, separate menu item)
	calendar: {
		routes: [
			{
				path: "/calendar",
				exact: true,
				component: () => import("./components/calendar/CalendarPage.jsx"),
				permissionModel: "calendarEvent"
			},
			{
				path: "/calendar/calendars",
				exact: true,
				component: () => import("./components/calendar/calendars.jsx"),
				permissionModel: "calendarCalendar"
			},
			{
				path: "/calendar/calendar/:id",
				component: () => import("./components/calendar/calendar.jsx"),
				permissionModel: "calendarCalendar"
			},
			{
				path: "/calendar/settings",
				exact: true,
				component: () => import("./components/calendar/CalendarSettings.jsx"),
				permissionModel: "calendarEvent"
			},
			{
				path: "/calendar/booking-links",
				exact: true,
				component: () => import("./components/calendar/BookingLinksPage.jsx"),
				permissionModel: "calendarEvent"
			},
			{
				path: "/calendar/:view",
				component: () => import("./components/calendar/CalendarPage.jsx"),
				permissionModel: "calendarEvent"
			}
		]
	},

	meetings: {
		routes: [
			{
				path: "/meetings",
				exact: true,
				component: () => import("./components/meetings/MeetingsPage.jsx"),
				permissionModel: "meetings"
			},
			{
				path: "/meetings/create",
				component: () => import("./components/meetings/MeetingsPage.jsx"),
				permissionModel: "meetings"
			},
			{
				path: "/meetings/:id/live",
				component: () => import("./components/meetings/MeetingsPage.jsx"),
				permissionModel: "meetings"
			},
			{
				path: "/meetings/:id",
				component: () => import("./components/meetings/MeetingsPage.jsx"),
				permissionModel: "meetings"
			}
		]
	},

	security: {
		routes: [
			{
				path: "/security/overview",
				component: () => import("./components/security/SecurityOverview.jsx"),
				permissionModel: "securityOverview"
			},
			{
				path: "/security/clients",
				component: () => import("./components/security/SecurityClients.jsx"),
				exact: true,
				permissionModel: "securityClients"
			},
			{
				path: "/security/devices",
				component: () => import("./components/security/SecurityDevices.jsx"),
				exact: true,
				permissionModel: "securityDevices"
			},
			{
				path: "/security/devices/:id",
				component: () => import("./components/security/SecurityDevice.jsx"),
				permissionModel: "securityDevices"
			},
			{
				path: "/security/surveillance-system/dashboard",
				component: () => import("./components/security/SurveillanceDashboard.jsx"),
				permissionModel: "surveillanceDashboard"
			},
			{
				path: "/security/alerts",
				component: () => import("./components/security/SecurityAlerts.jsx"),
				permissionModel: "securityAlerts"
			},
			{
				path: "/security/nvr",
				component: () => import("./components/security/NvrPage.jsx"),
				permissionModel: "nvr"
			},
			{
				path: "/security/cameras",
				component: () => import("./components/it/camera/cameras.jsx"),
				permissionModel: "pc"
			},
			{
				path: "/security/cameras/:id",
				component: () => import("./components/it/camera/camera.jsx"),
				permissionModel: "pc"
			},
			{
				path: "/security/sessions",
				component: () => import("./components/security/SecuritySessions.jsx"),
				exact: true,
				permissionModel: "securitySessions"
			},
			{
				path: "/security/sessions/:id",
				component: () => import("./components/security/SecuritySession.jsx"),
				permissionModel: "securitySessions"
			},
			{
				path: "/security/media",
				component: () => import("./components/security/SecurityMedia.jsx"),
				permissionModel: "securityMedia"
			},
			{
				path: "/security/policies",
				component: () => import("./components/security/SecurityPolicies.jsx"),
				exact: true,
				permissionModel: "securityPolicies"
			},
			{
				path: "/security/policies/new",
				component: () => import("./components/security/SecurityPolicyForm.jsx"),
				permissionModel: "securityPolicies"
			},
			{
				path: "/security/policies/:id",
				component: () => import("./components/security/SecurityPolicyForm.jsx"),
				permissionModel: "securityPolicies"
			},
			{
				path: "/security/ip-policy",
				component: () => import("./components/security/SecurityIpPolicy.jsx"),
				permissionModel: "securityIpPolicy"
			},
			{
				path: "/security/settings",
				component: () => import("./components/security/SecuritySettings.jsx"),
				permissionModel: "securitySettings"
			},
			{ path: "/security", exact: true, redirect: "/security/overview" }
		]
	},

	cac: {
		routes: [
			{
				path: "/automation/dashboard",
				component: () => import("./pages/Automation/AutomationDashboard.jsx"),
				permissionModel: "automation"
			},
			{
				path: "/automation/rules/new",
				component: () => import("./pages/Automation/AutomationRuleBuilder.jsx"),
				permissionModel: "automation"
			},
			{
				path: "/automation/rules/:id",
				component: () => import("./pages/Automation/AutomationRuleBuilder.jsx"),
				permissionModel: "automation",
				keyFromPathname: true
			},
			{
				path: "/automation/rules",
				component: () => import("./pages/Automation/AutomationDashboard.jsx"),
				permissionModel: "automation"
			},
			{
				path: "/automation/logs",
				component: () => import("./pages/Automation/AutomationLogs.jsx"),
				permissionModel: "automation"
			},
			{ path: "/automation", exact: true, redirect: "/automation/dashboard" }
		]
	},

	office: {
		routes: [
			{
				path: "/office/dashboard",
				exact: true,
				component: () => import("./components/office/OfficeDashboard.jsx"),
				permissionModel: "office.dashboard"
			},
			{
				path: "/office/infrastructure/locations",
				exact: true,
				component: () => import("./components/office/OfficeLocations.jsx"),
				permissionModel: "office.locations"
			},
			{
				path: "/office/infrastructure/locations/:id",
				exact: true,
				component: () => import("./components/office/OfficeLocationDetail.jsx"),
				permissionModel: "office.locations"
			},
			{
				path: "/office/infrastructure/floors",
				exact: true,
				component: () => import("./components/office/OfficeFloors.jsx"),
				permissionModel: "office.floors"
			},
			{
				path: "/office/infrastructure/floors/:id",
				exact: true,
				component: () => import("./components/office/OfficeFloorDetail.jsx"),
				permissionModel: "office.floors"
			},
			{
				path: "/office/infrastructure/rooms",
				exact: true,
				component: () => import("./components/office/OfficeRooms.jsx"),
				permissionModel: "office.rooms"
			},
			{
				path: "/office/infrastructure/rooms/:id",
				exact: true,
				component: () => import("./components/office/OfficeRoomDetail.jsx"),
				permissionModel: "office.rooms"
			},
			{
				path: "/office/infrastructure/desks",
				exact: true,
				component: () => import("./components/office/OfficeDesks.jsx"),
				permissionModel: "office.desks"
			},
			{
				path: "/office/infrastructure/desks/:id",
				exact: true,
				component: () => import("./components/office/OfficeDeskDetail.jsx"),
				permissionModel: "office.desks"
			},
			{
				path: "/office/infrastructure/floor-map",
				exact: true,
				component: () => import("./components/office/OfficeFloorMap.jsx"),
				permissionModel: "office.map"
			},
			{
				path: "/office/spaces/floors-maps",
				exact: true,
				component: () => import("./components/office/OfficeFloorsAndMaps.jsx"),
				permissionModel: "office.floors"
			},
			{
				path: "/office/bookings/reservations",
				exact: true,
				component: () => import("./components/office/BookingTimelinePage.jsx"),
				permissionModel: "office.bookings"
			},
			{
				path: "/office/reservations/booking-policies",
				exact: true,
				component: () => import("./components/office/BookingPolicies.jsx"),
				permissionModel: "office.bookingPolicies"
			},
			{
				path: "/office/reservations/resources",
				exact: true,
				component: () => import("./components/office/OfficeResources.jsx"),
				permissionModel: "office.resources"
			},
			{
				path: "/office/reservations/resources/:id",
				exact: true,
				component: () => import("./components/office/OfficeResourceDetail.jsx"),
				permissionModel: "office.resources"
			},
			{
				path: "/office/inventory/maintenance",
				exact: true,
				component: () => import("./components/office/inventory/OfficeMaintenancePage.jsx"),
				permissionModel: "office.inventoryMaintenance"
			},
			{
				path: "/office/operations/maintenance",
				exact: true,
				component: () => import("./components/office/inventory/OfficeMaintenancePage.jsx"),
				permissionModel: "office.inventoryMaintenance"
			},
			{
				path: "/office/operations/service-requests",
				exact: true,
				component: () => import("./components/office/OfficeServiceRequests.jsx"),
				permissionModel: "office.serviceRequests"
			},
			{
				path: "/office/inventory/settings",
				exact: true,
				component: () => import("./components/office/inventory/OfficeInventorySettings.jsx"),
				permissionModel: "office.inventorySettings"
			},
			{
				path: "/office/settings",
				exact: true,
				component: () => import("./components/office/inventory/OfficeInventorySettings.jsx"),
				permissionModel: "office.inventorySettings"
			},
			{
				path: "/office/settings/notifications",
				exact: true,
				component: () => import("./components/office/OfficeNotificationSettings.jsx"),
				permissionModel: "office.locations"
			},
			{
				path: "/office/settings/permissions",
				exact: true,
				component: () => import("./components/system/role/list.jsx"),
				permissionModel: "role"
			},
			{
				path: "/office/inventory/assets",
				exact: true,
				component: () => import("./components/office/inventory/OfficeInventoryPage.jsx"),
				permissionModel: "office.inventory"
			},
			{
				path: "/office/inventory/consumables",
				exact: true,
				component: () => import("./components/office/inventory/OfficeInventoryPage.jsx"),
				permissionModel: "office.inventory"
			},
			{
				path: "/office/inventory/stock",
				exact: true,
				component: () => import("./components/office/inventory/OfficeInventoryPage.jsx"),
				permissionModel: "office.inventory"
			},
			{
				path: "/office/inventory",
				exact: true,
				component: () => import("./components/office/inventory/OfficeInventoryPage.jsx"),
				permissionModel: "office.inventory"
			},
			{
				path: "/office/inventory/:id",
				exact: true,
				component: () => import("./components/office/inventory/OfficeInventoryDetail.jsx"),
				permissionModel: "office.inventory"
			},
			{
				path: "/office/infrastructure/rooms/:id/display",
				exact: true,
				component: () => import("./components/office/RoomDisplayPage.jsx"),
				permissionModel: "office.rooms"
			},
			// Backward-compatible aliases for detail/display pages, plus redirects for old lists.
			{ path: "/office/locations", exact: true, redirect: "/office/infrastructure/locations" },
			{ path: "/office/locations/:id", exact: true, component: () => import("./components/office/OfficeLocationDetail.jsx"), permissionModel: "office.locations" },
			{ path: "/office/floors", exact: true, redirect: "/office/infrastructure/floors" },
			{ path: "/office/floors/:id", exact: true, component: () => import("./components/office/OfficeFloorDetail.jsx"), permissionModel: "office.floors" },
			{ path: "/office/rooms", exact: true, redirect: "/office/infrastructure/rooms" },
			{ path: "/office/rooms/:id", exact: true, component: () => import("./components/office/OfficeRoomDetail.jsx"), permissionModel: "office.rooms" },
			{ path: "/office/rooms/:id/display", exact: true, component: () => import("./components/office/RoomDisplayPage.jsx"), permissionModel: "office.rooms" },
			{ path: "/office/desks", exact: true, redirect: "/office/infrastructure/desks" },
			{ path: "/office/desks/:id", exact: true, component: () => import("./components/office/OfficeDeskDetail.jsx"), permissionModel: "office.desks" },
			{ path: "/office/reservations/rooms", exact: true, redirect: "/office/bookings/reservations" },
			{ path: "/office/bookings/calendar", exact: true, redirect: "/office/bookings/reservations" },
			{ path: "/office/booking-timeline", exact: true, redirect: "/office/bookings/reservations" },
			{ path: "/office/resources", exact: true, redirect: "/office/reservations/resources" },
			{ path: "/office/resources/:id", exact: true, component: () => import("./components/office/OfficeResourceDetail.jsx"), permissionModel: "office.resources" },
			{ path: "/office/map", exact: true, redirect: "/office/infrastructure/floor-map" },
			{ path: "/office", exact: true, redirect: "/office/dashboard" }
		]
	}
};

// Lazy component wrapper - Standard routes
const resolveAttendanceTab = (location) => {
	if (location?.pathname === "/hr/attendance/reports") return "reports";
	if (location?.pathname === "/hr/attendance/calendar") return "calendar";

	const requestedTab = new URLSearchParams(location?.search || "").get("tab");
	return requestedTab === "manualEntry" || requestedTab === "dayOff" ? requestedTab : "attendance";
};

const AttendanceRouteFallback = ({ location }) => {
	const { lang } = useLang();

	return (
		<>
			<AttendanceTopNav lang={lang} active={resolveAttendanceTab(location)} />
			<Loading />
		</>
	);
};

const LazyRoute = ({
	path,
	componentImport,
	exact = false,
	permissionModel,
	permissionCheck,
	keyFromPathname = false,
	preserveAttendanceTabs = false,
	financeWorkspace = false
}) => {
	const lazyRef = useRef(null);
	if (!lazyRef.current) lazyRef.current = lazy(componentImport);
	const LazyComponent = lazyRef.current;
	const { user } = useUser();

	return (
		<Route
			exact={exact}
			path={path}
			render={(props) => {
				if (
					permissionCheck
						? !permissionCheck(user)
						: permissionModel && !userHasModuleMenuAccess(user, permissionModel)
				) {
					return <Redirect to="/404" />;
				}
				const remountKey = keyFromPathname ? props.location?.pathname : undefined;
				return (
					<Suspense
						fallback={
							preserveAttendanceTabs ? <AttendanceRouteFallback location={props.location} /> : <Loading />
						}
					>
						{financeWorkspace && <FinanceWorkspaceBar />}
						<LazyComponent key={remountKey} {...props} />
					</Suspense>
				);
			}}
		/>
	);
};

// Lazy component wrapper - AI routes with layout
const LazyAiRoute = ({ path, componentImport, exact = false, permissionModel }) => {
	const lazyRef = useRef(null);
	if (!lazyRef.current) lazyRef.current = lazy(componentImport);
	const LazyComponent = lazyRef.current;
	const { user } = useUser();

	return (
		<Route
			exact={exact}
			path={path}
			render={(props) => {
				if (permissionModel && !userHasModuleMenuAccess(user, permissionModel)) {
					return <Redirect to="/404" />;
				}
				return (
					<Suspense fallback={<Loading />}>
						<AiChatLayout>
							<LazyComponent {...props} />
						</AiChatLayout>
					</Suspense>
				);
			}}
		/>
	);
};

// Main router component with environment-based loading
const EnvironmentBasedRouter = () => {
	const { user } = useUser();
	const [availableRoutes, setAvailableRoutes] = useState([]);

	useEffect(() => {
		const modules = getEnabledModules();

		let routes = [];

		if (modules.includes("all")) {
			Object.keys(moduleDefinitions).forEach((moduleName) => {
				if (!shouldIncludeModuleRoutes(moduleName)) return;
				routes = [...routes, ...moduleDefinitions[moduleName].routes];
			});
		} else {
			modules.forEach((moduleName) => {
				if (!shouldIncludeModuleRoutes(moduleName)) return;
				if (moduleDefinitions[moduleName]) {
					routes = [...routes, ...moduleDefinitions[moduleName].routes];
				} else {
					console.warn(`Module "${moduleName}" not found in moduleDefinitions`);
				}
			});

			// Always-on modules: register even when not in the enabled whitelist.
			// (The sidebar entry is config-driven and shown independently, so these
			// must be routable everywhere or their menu items would 404.)
			ALWAYS_ON_MODULES.forEach((moduleName) => {
				if (!modules.includes(moduleName) && moduleDefinitions[moduleName]) {
					routes = [...routes, ...moduleDefinitions[moduleName].routes];
				}
			});
		}

		routes.push({
			path: "/home",
			component: () => import("./components/home.jsx"),
			exact: true
		});

		routes.push({
			path: "/trash",
			component: () => import("./components/trash/TrashPage.jsx"),
			exact: true
		});

		setAvailableRoutes(routes);
	}, []);

	if (!user) {
		return <Route path="*" component={lazy(() => import("./components/Page404.jsx"))} />;
	}

	return (
		<Suspense fallback={<Loading />}>
			<Switch>
				<Redirect exact from="/" to="/home" />
				<Route exact path="/404" component={lazy(() => import("./components/Page404.jsx"))} />

				{availableRoutes.map((route, index) => {
					// Redirect routes
					if (route.redirect) {
						return (
							<Redirect
								key={`${route.path}-${index}`}
								exact={route.exact !== false}
								from={route.path}
								to={route.redirect}
							/>
						);
					}

					// Use special AI layout wrapper for AI routes
					if (route.useAiLayout) {
						return (
							<LazyAiRoute
								key={`${route.path}-${index}`}
								path={route.path}
								componentImport={route.component}
								exact={route.exact !== false}
								permissionModel={route.permissionModel}
							/>
						);
					}

					return (
						<LazyRoute
							key={`${route.path}-${index}`}
							path={route.path}
							componentImport={route.component}
							exact={route.exact !== false}
							permissionModel={route.permissionModel}
							permissionCheck={route.permissionCheck}
							keyFromPathname={Boolean(route.keyFromPathname)}
							preserveAttendanceTabs={Boolean(route.preserveAttendanceTabs)}
							financeWorkspace={Boolean(route.financeWorkspace)}
						/>
					);
				})}

				<Route path="*" component={lazy(() => import("./components/Page404.jsx"))} />
			</Switch>
		</Suspense>
	);
};

// Hook to check if a module is enabled
const useModuleEnabled = (moduleName) => {
	const enabledModules = getEnabledModules();
	return enabledModules.includes("all") || enabledModules.includes(moduleName.toLowerCase());
};

// Component to conditionally render UI elements based on enabled modules
const ConditionalModule = ({ module, children, fallback = null }) => {
	const isEnabled = useModuleEnabled(module);

	if (!isEnabled) {
		return fallback;
	}

	return <>{children}</>;
};

export {
	EnvironmentBasedRouter,
	useModuleEnabled,
	ConditionalModule,
	getEnabledModules,
	moduleDefinitions
};
